import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Search,
  Refresh,
  Receipt,
  Person,
  Email,
  Phone,
  Event,
  AttachMoney,
  CalendarToday,
  Visibility,
} from '@mui/icons-material';
import { orderApi } from '../services/api';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const failedStatuses = ['failed', 'cancelled', 'canceled', 'rejected', 'expired'];

  useEffect(() => {
    const isNotFailed = (order) => {
      const status = (order.status || order.payment_status || '').toLowerCase();
      return !failedStatuses.includes(status);
    };

    if (!searchTerm.trim()) {
      setFilteredOrders(orders.filter(isNotFailed));
    } else {
      const filtered = orders.filter(order => {
        if (!isNotFailed(order)) return false;
        const searchLower = searchTerm.toLowerCase();
        return (
          order.order_number?.toLowerCase().includes(searchLower) ||
          order.customer_name?.toLowerCase().includes(searchLower) ||
          order.customer_email?.toLowerCase().includes(searchLower) ||
          order.customer_phone?.toLowerCase().includes(searchLower) ||
          order.activity_name?.toLowerCase().includes(searchLower) ||
          order.event_name?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredOrders(filtered);
    }
  }, [searchTerm, orders]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use orderApi from centralized service
      const data = await orderApi.getOrderFeed();
      
      // Handle different response structures
      let ordersList = [];
      if (Array.isArray(data)) {
        ordersList = data;
      } else if (Array.isArray(data.data)) {
        ordersList = data.data;
      } else if (Array.isArray(data.orders)) {
        ordersList = data.orders;
      } else if (data.data?.orders && Array.isArray(data.data.orders)) {
        ordersList = data.data.orders;
      } else if (data.data?.data && Array.isArray(data.data.data)) {
        ordersList = data.data.data;
      }
      
      console.log('Fetched orders:', ordersList);
      setOrders(ordersList);
      setFilteredOrders(ordersList);
    } catch (err) {
      console.error('Error fetching orders:', err);
      const apiErrorMsg = err.response?.data?.message || err.response?.data?.error || err.message;
      setError(apiErrorMsg || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (order) => {
    try {
      setDetailsOpen(true);
      setSelectedOrder(order);
      
      // Try to fetch full order details if order number is available
      if (order.order_number) {
        try {
          const details = await orderApi.getOrderDetails(order.order_number);
          setSelectedOrder(details.data || details || order);
        } catch (detailsError) {
          console.warn('Could not fetch order details, using basic order info:', detailsError);
          setSelectedOrder(order);
        }
      }
    } catch (error) {
      console.error('Error viewing order details:', error);
      setSelectedOrder(order);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedOrder(null);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'paid':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'cancelled':
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('ka-GE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatPrice = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `${amount} ₾`;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Orders
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage all orders
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={fetchOrders}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      {/* Search and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Search orders by order number, customer name, email, phone, or activity..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchOrders}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Orders Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Order Number</strong></TableCell>
                  <TableCell><strong>Customer</strong></TableCell>
                  <TableCell><strong>Activity/Event</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>People</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {searchTerm ? 'No orders found matching your search' : 'No orders found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order) => (
                    <TableRow key={order.id || order.order_number || Math.random()}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {order.order_number || order.number || order.id || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {order.customer_name || order.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.customer_email || order.email || ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {order.activity_name || order.event_name || order.service_name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {formatDate(order.event_date || order.created_at || order.date)}
                      </TableCell>
                      <TableCell>
                        {order.people_count || order.quantity || order.participants || 1}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          {formatPrice(order.total_amount || order.amount || order.price)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status || order.payment_status || 'Unknown'}
                          color={getStatusColor(order.status || order.payment_status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(order)}
                          color="primary"
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Receipt />
            Order Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Order Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Order Number
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedOrder.order_number || selectedOrder.number || selectedOrder.id || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedOrder.status || selectedOrder.payment_status || 'Unknown'}
                    color={getStatusColor(selectedOrder.status || selectedOrder.payment_status)}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedOrder.created_at || selectedOrder.date)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Payment Method
                  </Typography>
                  <Typography variant="body1">
                    {selectedOrder.payment_method || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Customer Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Person fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Name
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {selectedOrder.customer_name || selectedOrder.name || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Email fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {selectedOrder.customer_email || selectedOrder.email || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Phone fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {selectedOrder.customer_phone || selectedOrder.phone || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Country
                  </Typography>
                  <Typography variant="body1">
                    {selectedOrder.customer_country || selectedOrder.country || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Booking Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Event fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Activity/Event
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {selectedOrder.activity_name || selectedOrder.event_name || selectedOrder.service_name || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CalendarToday fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Event Date
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {formatDate(selectedOrder.event_date || selectedOrder.date)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Number of People
                  </Typography>
                  <Typography variant="body1">
                    {selectedOrder.people_count || selectedOrder.quantity || selectedOrder.participants || 1}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AttachMoney fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="primary" fontWeight={600}>
                    {formatPrice(selectedOrder.total_amount || selectedOrder.amount || selectedOrder.price)}
                  </Typography>
                </Grid>
                
                {selectedOrder.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {selectedOrder.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Orders;

